import type { GroupDriesSession, Run, SessionEvent } from "@/types";

/**
 * Where an actor needed a line.
 *
 * A "dry" is a prompt the teacher sent to an actor (`prompt_sent`) or a line
 * the actor's card asked for (`line_requested`), recorded against the beat
 * they were holding. The record is information, never a score: these say
 * where and how often, and nothing about how well.
 *
 * **A dry is a beat, not an event.** An actor who asks for the line and is
 * then prompted dried once; so did one prompted twice on the same line.
 * Counting events would report the first as two dries and punish the
 * teacher's generosity. So each beat counts once per actor, and an event
 * with no beat behind it — a prompt sent while nobody was on — counts as one
 * dry of its own. The app's debrief applies the same rule (`driesFrom`), so
 * the number a teacher saw on the tablet is the number they read here.
 *
 * Pure, so the page stays a page and the rule can be tested without one.
 */

export interface Dry {
  /** 1-based position of the beat in the run, or undefined when the beat is not in it. */
  beat?: number;
  cueId?: string;
  /** The line the actor was holding, from the run's own copy of the script. */
  line: string;
  /** Whether the card asked for the line on this beat. */
  asked: boolean;
  /** Whether the teacher prompted on this beat, with or without words. */
  prompted: boolean;
  /** What the teacher sent on this beat, in order. Empty when nothing was. */
  prompts: string[];
  /** When the beat was first dried on. */
  at: number;
}

export interface ActorDries {
  uid: string;
  roleId: string;
  dries: Dry[];
  /** Beats a prompt was sent on. */
  prompted: number;
  /** Beats the line was asked for on. */
  asked: number;
}

export interface BeatMark {
  beat: number;
  cueId: string;
  roleId: string;
  dried: boolean;
  prompted: boolean;
  asked: boolean;
}

function isDry(event: SessionEvent): boolean {
  return event.type === "prompt_sent" || event.type === "line_requested";
}

/** What a dry is counted by: the beat, or — with no beat behind it — the event itself. */
function dryKey(event: SessionEvent): string {
  return event.cueId ?? `event:${event.id}`;
}

function text(payload: Record<string, unknown> | undefined): string | undefined {
  const value = payload?.text;
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

/** The events of one run that are dries, in order. */
export function driesOf(run: Pick<Run, "id">, events: readonly SessionEvent[]): SessionEvent[] {
  return events.filter((event) => event.runId === run.id && isDry(event));
}

/** How many dries a list of one actor's events amounts to, by the rule above. */
export function countDries(events: readonly SessionEvent[]): number {
  return new Set(events.filter(isDry).map(dryKey)).size;
}

/**
 * The dries of one run, per actor in the cast, in cast order.
 *
 * A prompt sent to everybody (`to: "all"`) has no uid and is nobody's dry.
 * An actor who dried on a beat that is no longer in the run — a live edit —
 * keeps the dry with no beat number and no line.
 */
export function driesByActor(run: Run, events: readonly SessionEvent[]): ActorDries[] {
  const index = new Map(run.cues.map((cue, position) => [cue.id, position]));
  const byUid = new Map<string, Map<string, Dry>>();

  for (const event of driesOf(run, events)) {
    if (!event.uid) continue;
    const beats = byUid.get(event.uid) ?? new Map<string, Dry>();
    const key = dryKey(event);

    let dry = beats.get(key);
    if (!dry) {
      const position = event.cueId !== undefined ? index.get(event.cueId) : undefined;
      const cue = position !== undefined ? run.cues[position] : undefined;
      dry = { line: cue?.situation ?? "", asked: false, prompted: false, prompts: [], at: event.at };
      if (position !== undefined) dry.beat = position + 1;
      if (event.cueId !== undefined) dry.cueId = event.cueId;
      beats.set(key, dry);
    }

    if (event.type === "line_requested") dry.asked = true;
    else {
      dry.prompted = true;
      const sent = text(event.payload);
      if (sent !== undefined) dry.prompts.push(sent);
    }
    byUid.set(event.uid, beats);
  }

  return run.cast.map((seat) => {
    const dries = [...(byUid.get(seat.uid)?.values() ?? [])].sort(
      (a, b) => (a.beat ?? Infinity) - (b.beat ?? Infinity) || a.at - b.at
    );
    return {
      uid: seat.uid,
      roleId: seat.roleId,
      dries,
      prompted: dries.filter((dry) => dry.prompted).length,
      asked: dries.filter((dry) => dry.asked).length,
    };
  });
}

/** One mark per beat of the script: whether it was dried on, and how. */
export function beatMarks(run: Run, events: readonly SessionEvent[]): BeatMark[] {
  const marks = run.cues.map((cue, position) => ({
    beat: position + 1,
    cueId: cue.id,
    roleId: cue.roleId,
    dried: false,
    prompted: false,
    asked: false,
  }));
  const byCue = new Map(marks.map((mark) => [mark.cueId, mark]));

  for (const event of driesOf(run, events)) {
    const mark = event.cueId !== undefined ? byCue.get(event.cueId) : undefined;
    if (!mark) continue;
    mark.dried = true;
    if (event.type === "prompt_sent") mark.prompted = true;
    else mark.asked = true;
  }
  return marks;
}

/** Whether a run has anything for the dry map to say. */
export function hasDries(run: Pick<Run, "id">, events: readonly SessionEvent[]): boolean {
  return driesOf(run, events).length > 0;
}

export interface LessonDries {
  sessionId: string;
  startedAt: string;
  runId: string;
  /** How many beats the run had. */
  beats: number;
  dries: number;
}

/**
 * A student's dries per scripted run, lesson by lesson, oldest first.
 *
 * What a group's history shows as off-book progress: a line of counts a
 * teacher reads left to right. Students who were in no scripted run are not
 * in the map. Counted by the rule above — a beat once, whatever happened on
 * it — and a prompt sent to everybody counts for nobody.
 */
export function driesAcrossLessons(
  sessions: readonly GroupDriesSession[]
): Map<string, LessonDries[]> {
  const byUid = new Map<string, LessonDries[]>();
  const ordered = [...sessions].sort((a, b) => a.startedAt.localeCompare(b.startedAt));

  for (const session of ordered) {
    for (const run of session.runs) {
      const dries = session.dries.filter(
        (event) => event.runId === run.id && event.uid !== undefined && isDry(event)
      );
      for (const seat of run.cast) {
        const entry: LessonDries = {
          sessionId: session.id,
          startedAt: session.startedAt,
          runId: run.id,
          beats: run.beats,
          dries: countDries(dries.filter((event) => event.uid === seat.uid)),
        };
        byUid.set(seat.uid, [...(byUid.get(seat.uid) ?? []), entry]);
      }
    }
  }
  return byUid;
}
